import { type DisplayMode, items, type LineItem, type Slip, visibleSections } from './slips'

/**
 * The item counter answers one question across every open order: what is still to be
 * made, and how much of it. "Six chicken burritos left" is a different thing from six
 * tickets each mentioning one.
 *
 * The menu the guest buys from is not the menu the kitchen makes. A meal is one product
 * to the till and three things to the kitchen; a large and a small of the same burrito
 * are one line to the kitchen and two products to the till. The mappings below are how
 * one is translated into the other.
 */

export type Mapping = { productName: string; ratio: number }
export type ComboMapping = {
  productName: string
  optionName: string
  countAs: string
  ratio: number
}

export type ItemCounterConfig = {
  enabled: boolean
  printWorkflowEnabled: boolean
  excludedProductNames: string[]
  optionMappings: Record<string, Mapping>
  productMappings: Record<string, Mapping>
  bundleMappings: Record<string, Mapping[]>
  comboMappings: ComboMapping[]
  autoCompleteProductNames: string[]
}

export const NO_COUNTER: ItemCounterConfig = {
  enabled: false,
  printWorkflowEnabled: false,
  excludedProductNames: [],
  optionMappings: {},
  productMappings: {},
  bundleMappings: {},
  comboMappings: [],
  autoCompleteProductNames: [],
}

type Row = { [key: string]: unknown } | null | undefined

function strings(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []
}
function mapping(value: unknown): Mapping | null {
  if (!value || typeof value !== 'object') return null
  const row = value as Record<string, unknown>
  if (typeof row.productName !== 'string') return null
  const ratio = typeof row.ratio === 'number' && Number.isFinite(row.ratio) ? row.ratio : 1
  return { productName: row.productName, ratio }
}
function mappingRecord(value: unknown): Record<string, Mapping> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const out: Record<string, Mapping> = {}
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const parsed = mapping(raw)
    if (parsed) out[key] = parsed
  }
  return out
}

export function readItemCounterConfig(row: Row): ItemCounterConfig {
  if (!row) return NO_COUNTER
  const bundles: Record<string, Mapping[]> = {}
  const rawBundles = row.bundleMappings
  if (rawBundles && typeof rawBundles === 'object' && !Array.isArray(rawBundles))
    for (const [key, raw] of Object.entries(rawBundles as Record<string, unknown>)) {
      const parts = Array.isArray(raw) ? raw.map(mapping).filter((part): part is Mapping => part != null) : []
      if (parts.length) bundles[key] = parts
    }
  const combos = Array.isArray(row.comboMappings)
    ? row.comboMappings.flatMap((raw) => {
        if (!raw || typeof raw !== 'object') return []
        const combo = raw as Record<string, unknown>
        if (typeof combo.productName !== 'string' || typeof combo.optionName !== 'string' || typeof combo.countAs !== 'string') return []
        return [
          {
            productName: combo.productName,
            optionName: combo.optionName,
            countAs: combo.countAs,
            ratio: typeof combo.ratio === 'number' && Number.isFinite(combo.ratio) ? combo.ratio : 1,
          },
        ]
      })
    : []
  return {
    enabled: row.enabled === true,
    printWorkflowEnabled: row.printWorkflowEnabled === true,
    excludedProductNames: strings(row.excludedProductNames),
    optionMappings: mappingRecord(row.optionMappings),
    productMappings: mappingRecord(row.productMappings),
    bundleMappings: bundles,
    comboMappings: combos,
    autoCompleteProductNames: strings(row.autoCompleteProductNames),
  }
}

/**
 * A restaurant that has its own record uses it and only it. Half of one and half of the
 * other would be a configuration nobody wrote and nobody could reason about.
 */
export function effectiveCounterConfig(shared: Row, own: Row): ItemCounterConfig {
  return own ? readItemCounterConfig(own) : readItemCounterConfig(shared)
}

export type CountedLine = { name: string; quantity: number }

function add(into: Map<string, number>, name: string, quantity: number) {
  if (quantity <= 0) return
  into.set(name, (into.get(name) ?? 0) + quantity)
}

/**
 * What one line on a ticket means to the kitchen. A meal becomes its parts, a product
 * that is really another product becomes that one, and anything the chain has excluded
 * becomes nothing at all.
 */
export function countLineItem(item: LineItem, config: ItemCounterConfig, into: Map<string, number>) {
  const name = item.name ?? ''
  const count = item.count ?? 1
  if (!name || count <= 0) return
  if (config.excludedProductNames.includes(name)) return

  const options = (item.alternativeItems ?? [])
    .filter((option) => option != null && option.isChosen !== false && (option.count ?? 1) > 0)
    .map((option) => option?.name ?? '')
    .filter(Boolean)

  // A combination of this product with this choice is its own thing to the kitchen, and
  // the choice must not then be counted a second time on its own.
  const consumed = new Set<string>()
  for (const option of options) {
    const combo = config.comboMappings.find((rule) => rule.productName === name && rule.optionName === option)
    if (!combo) continue
    add(into, combo.countAs, count * combo.ratio)
    consumed.add(option)
  }

  const bundle = config.bundleMappings[name]
  if (bundle) {
    for (const part of bundle) add(into, part.productName, count * part.ratio)
  } else {
    const mapped = config.productMappings[name]
    if (mapped) add(into, mapped.productName, count * mapped.ratio)
    else add(into, name, count)
  }

  for (const option of options) {
    if (consumed.has(option)) continue
    const mapped = config.optionMappings[option]
    if (mapped) add(into, mapped.productName, count * mapped.ratio)
  }
}

/**
 * Count all food on outstanding tickets. Shared line preparation timestamps cannot
 * complete this station’s work; station Done removes the ticket from its API queue.
 */
export function countOutstanding(slips: Slip[], mode: Exclude<DisplayMode, 'auto'>, config: ItemCounterConfig, includeAlternatives = false, displayId?: string): CountedLine[] {
  const totals = new Map<string, number>()
  for (const slip of slips) {
    if (slip.readyForPickup === true) continue
    for (const section of visibleSections(slip, mode))
      for (const item of items(section)) {
        if (item.preparationCompletedAt && (!item.preparationBy || item.preparationBy.id === displayId || displayId === 'main')) continue
        const active = config.enabled ? config : NO_COUNTER
        if (includeAlternatives && !active.enabled) {
          const suffix = (item.alternativeItems ?? [])
            .filter((option) => option && option.isChosen !== false && (option.count ?? 1) > 0)
            .map((option) => option?.name)
            .filter(Boolean)
            .join(', ')
          countLineItem({ ...item, name: suffix ? `${item.name} · ${suffix}` : item.name }, active, totals)
        } else countLineItem(item, active, totals)
      }
  }
  return [...totals]
    .map(([name, quantity]) => ({ name, quantity: Math.round(quantity * 100) / 100 }))
    .filter((line) => line.quantity > 0)
    .sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name, 'en'))
}

/**
 * Lines the chain has said finish themselves. They are still shown — the kitchen should
 * see a drink on the ticket — but they do not hold up finishing the order.
 */
export function blocksCompletion(item: LineItem, config: ItemCounterConfig): boolean {
  const name = item.name ?? ''
  return !config.autoCompleteProductNames.includes(name)
}
